/**
 * Загрузка файла с прогрессом передачи: multipart через XMLHttpRequest.
 *
 * `fetch` (и вместе с ним RTK Query) не сообщает, сколько байт уже ушло, а книга
 * ЭММ весит 5 МБ и больше: на медленном канале человек минуту смотрит на
 * неподвижный экран. Поэтому для таких загрузок — XHR с `upload.onprogress`.
 * Авторизация та же, что у `baseQueryWithRefresh`: токен из store, на 401 — одно
 * обновление токена (общий мьютекс `refreshAccessToken`) и повтор запроса.
 * Ответ разбирается как в `parseResponse`: HTML-отказ nginx (413, 502, 504)
 * превращается в ту же форму `{error: {code, message}}`, что отдаёт backend.
 */
import type { BaseQueryApi, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { AppDispatch, RootState } from '@app/store';
import { refreshAccessToken } from '@features/auth/model/refresh';
import { infraError } from './parseResponse';

const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** Где загрузка: файл передаётся (байты) или передан и сервер его разбирает. */
export type UploadProgress =
  { phase: 'upload'; loaded: number; total: number } | { phase: 'processing' };

type Sent =
  { kind: 'response'; status: number; body: unknown } | { kind: 'network' } | { kind: 'aborted' };

function parseBody(xhr: XMLHttpRequest): unknown {
  const isJson = (xhr.getResponseHeader('content-type') ?? '').includes('application/json');
  const text = xhr.responseText;
  if (isJson) {
    try {
      return text ? JSON.parse(text) : null;
    } catch {
      return infraError(xhr.status);
    }
  }
  if (xhr.status >= 200 && xhr.status < 300) return text;
  console.warn(`Не-JSON ответ ${xhr.status} от ${xhr.responseURL}:`, text.slice(0, 200));
  return infraError(xhr.status);
}

function send(
  url: string,
  form: FormData,
  token: string | null,
  onProgress: ((p: UploadProgress) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<Sent> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve({ kind: 'aborted' });
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.({ phase: 'upload', loaded: e.loaded, total: e.total });
    };
    // Файл ушёл целиком — дальше ждём разбора на сервере.
    xhr.upload.onload = () => onProgress?.({ phase: 'processing' });
    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    const done = (sent: Sent) => {
      signal?.removeEventListener('abort', onAbort);
      resolve(sent);
    };
    xhr.onload = () => done({ kind: 'response', status: xhr.status, body: parseBody(xhr) });
    xhr.onerror = () => done({ kind: 'network' });
    xhr.ontimeout = () => done({ kind: 'network' });
    xhr.onabort = () => done({ kind: 'aborted' });
    xhr.send(form);
  });
}

/**
 * Отправить форму POST-запросом с прогрессом — для `queryFn` мутации RTK Query.
 * `form` — фабрика: при повторе после 401 FormData собирается заново.
 */
export async function uploadWithProgress(
  api: Pick<BaseQueryApi, 'getState' | 'dispatch' | 'signal'>,
  path: string,
  form: () => FormData,
  onProgress?: (p: UploadProgress) => void,
): Promise<{ data: unknown } | { error: FetchBaseQueryError }> {
  // getState у BaseQueryApi типизирован как unknown, а это store приложения —
  // тот же приём, что в baseQueryWithRefresh.
  const getState = api.getState as () => RootState;
  // dispatch BaseQueryApi — ThunkDispatch store приложения; refresh ждёт AppDispatch.
  const dispatch = api.dispatch as AppDispatch;
  const url = `${API_BASE}${path}`;

  let sent = await send(url, form(), getState().auth.accessToken, onProgress, api.signal);
  if (sent.kind === 'response' && sent.status === 401) {
    const ok = await refreshAccessToken(dispatch, getState);
    if (ok) sent = await send(url, form(), getState().auth.accessToken, onProgress, api.signal);
  }

  if (sent.kind === 'aborted') return { error: { status: 'FETCH_ERROR', error: 'AbortError' } };
  if (sent.kind === 'network') {
    return { error: { status: 'FETCH_ERROR', error: 'Нет связи с сервером' } };
  }
  if (sent.status >= 200 && sent.status < 300) return { data: sent.body };
  return { error: { status: sent.status, data: sent.body } };
}
